import { api } from "../api";
import { clearAnamneseEvaluation, EVALUATION_STORAGE_KEY } from "./anamneseEvaluationStore";

export const CONFIG_STORAGE_KEY = "anamneseConfig";
const DEFAULT_CONFIG = Object.freeze({ version: "v1", audiences: ["adulto"] });

const LEGACY_ADULT_COLLECTION = [
  /^\/questionario\/adulto(?:\/|$)/,
  /^\/questionario\/adulto-triagem/,
  /^\/questionario\/adulto-nutri/,
  /^\/questionario\/adulto-mapa/,
  /^\/mapa-da-saude/,
];

const LEGACY_BY_AUDIENCE = {
  adulto: "/questionario/adulto",
  kids: "/questionario/my-kids",
  baby: "/questionario/my-baby",
};

function readStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  const encoded = JSON.stringify(value);
  try {
    localStorage.setItem(key, encoded);
  } catch {
    // noop
  }
  try {
    localStorage.setItem(key, encoded);
  } catch {
    // noop
  }
}

function foldAudienceToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function normalizeAudience(value) {
  const hay = foldAudienceToken(value);
  if (!hay) return null;
  if (hay === "adulto" || hay === "adult" || hay.includes("adulto")) return "adulto";
  if (hay.includes("kids") || hay.includes("crianc")) return "kids";
  if (hay.includes("baby") || hay.includes("beb")) return "baby";
  return null;
}

export function normalizeAudienceList(audiences) {
  const list = [];
  (Array.isArray(audiences) ? audiences : []).forEach((item) => {
    const next = normalizeAudience(item);
    if (next && !list.includes(next)) list.push(next);
  });
  return list.length ? list : ["adulto"];
}

export function audienceFromGrupoSlug(grupoSlug) {
  const hay = foldAudienceToken(grupoSlug);
  if (hay.includes("baby") || hay.includes("beb")) return "baby";
  if (hay.includes("kids") || hay.includes("crianc")) return "kids";
  return "adulto";
}

export function defaultAnamneseConfig() {
  return { ...DEFAULT_CONFIG };
}

export function readStoredAnamneseConfig() {
  const stored = readStorage(CONFIG_STORAGE_KEY);
  if (!stored || typeof stored !== "object") return null;
  if (stored.version !== "v1" && stored.version !== "v2") return null;
  return {
    version: stored.version,
    audiences: normalizeAudienceList(stored.audiences),
  };
}

export function persistAnamneseConfig(payload) {
  if (!payload || (payload.version !== "v1" && payload.version !== "v2")) {
    return readStoredAnamneseConfig() || defaultAnamneseConfig();
  }
  const next = {
    version: payload.version,
    audiences: normalizeAudienceList(payload.audiences),
    savedAt: Date.now(),
  };
  const previous = readStoredAnamneseConfig();
  if (previous?.version && previous.version !== next.version) {
    clearAnswersOnVersionChange();
  }
  writeStorage(CONFIG_STORAGE_KEY, next);
  return { version: next.version, audiences: next.audiences };
}

function clearAnswersOnVersionChange() {
  try {
    localStorage.removeItem("userAnswers");
  } catch {
    // noop
  }
  clearAnamneseEvaluation();
}

function resolvedConfig(config) {
  return config || readStoredAnamneseConfig() || defaultAnamneseConfig();
}

export function isVersionV2(config = readStoredAnamneseConfig()) {
  return resolvedConfig(config).version === "v2";
}

export function hasV2Audience(audience, config = readStoredAnamneseConfig()) {
  const key = normalizeAudience(audience);
  if (!key || !isVersionV2(config)) return false;
  return resolvedConfig(config).audiences.includes(key);
}

export function isAdultAudience(audiences = defaultAnamneseConfig().audiences) {
  return normalizeAudienceList(audiences).includes("adulto");
}

export function isAnamneseV2(config = readStoredAnamneseConfig()) {
  return hasV2Audience("adulto", config);
}

/**
 * Fluxo de lançamento (LP): `launchPin` no browser marca cupom/contexto do LP.
 * A coleta segue a versão ativa (v1 ou v2) — não força mais o legado.
 */
export function isLaunchAnamneseFlow() {
  try {
    return Boolean(localStorage.getItem("launchPin"));
  } catch {
    return false;
  }
}

export function isLegacyAdultCollectionPath(pathname = "") {
  const path = String(pathname || "").split("?")[0];
  if (/\/questionario\/my-(kids|baby)/.test(path)) return false;
  return LEGACY_ADULT_COLLECTION.some((pattern) => pattern.test(path));
}

export function isV2CollectionPath(pathname = "") {
  const path = String(pathname || "").split("?")[0];
  return path === "/anamnese-v2" || path.startsWith("/anamnese-v2/");
}

function pathAudience(pathname = "") {
  const path = String(pathname || "").split("?")[0];
  if (/\/questionario\/my-kids/.test(path)) return "kids";
  if (/\/questionario\/my-baby/.test(path)) return "baby";
  if (isLegacyAdultCollectionPath(path)) return "adulto";
  return null;
}

export function collectionPath(audience = "adulto", { personaId, config, fresh = false } = {}) {
  const key = normalizeAudience(audience) || "adulto";
  const params = new URLSearchParams();
  if (hasV2Audience(key, config)) {
    if (key !== "adulto") params.set("audience", key);
    if (personaId) params.set("persona_id", String(personaId));
    // Começar agora / nova persona: perfil em branco, sem amarrar owner/rascunho.
    if (fresh && !personaId) params.set("fresh", "1");
    const qs = params.toString();
    return qs ? `/anamnese-v2?${qs}` : "/anamnese-v2";
  }
  return LEGACY_BY_AUDIENCE[key] || LEGACY_BY_AUDIENCE.adulto;
}

export function adultCollectionPath(config) {
  return collectionPath("adulto", { config });
}

export function legacyCollectionPath(audience = "adulto") {
  const key = normalizeAudience(audience) || "adulto";
  return LEGACY_BY_AUDIENCE[key] || LEGACY_BY_AUDIENCE.adulto;
}

export function adultMapPath(submissionUuid, config) {
  if (!isAnamneseV2(config)) return "/mapa-da-saude";
  return submissionUuid
    ? `/anamnese-v2/mapa?uuid=${encodeURIComponent(submissionUuid)}`
    : "/anamnese-v2/mapa";
}

/**
 * Funil comercial pós-mapa / pós-complete na v2 (ou preview interno).
 * Kids/baby usam as rotas de produto específicas; adulto mantém /solucoes-purissima.
 */
export function commercialSolutionsPath(audience = "adulto", { isPreview = false } = {}) {
  if (isPreview) return "/anamnese-v2/solucoes";
  const key = normalizeAudience(audience) || "adulto";
  if (key === "kids") return "/solucoes-kids";
  if (key === "baby") return "/solucoes-baby";
  return "/solucoes-purissima";
}

/**
 * Resolve o público do fluxo a partir de URL, state, store ou slug do questionário.
 */
export function resolveFlowAudience(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;

    if (typeof candidate === "object") {
      const explicit = normalizeAudience(candidate.audience);
      if (explicit) return explicit;

      const slug =
        candidate.questionnaire_slug ||
        candidate.questionnaire?.slug ||
        candidate.slug ||
        null;
      if (slug) return audienceFromGrupoSlug(slug);
      continue;
    }

    const next = normalizeAudience(candidate);
    if (next) return next;
  }

  return "adulto";
}

/** Query string comum das rotas internas da anamnese v2. */
export function anamneseFlowSearch({ audience, isPreview, uuid } = {}) {
  const params = new URLSearchParams();
  const key = normalizeAudience(audience);
  if (key && key !== "adulto") params.set("audience", key);
  if (isPreview) params.set("is_preview", "1");
  if (uuid) params.set("uuid", String(uuid));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function submissionRunnerPath(uuid, { audience, isPreview } = {}) {
  return `/anamnese-v2/submissoes/${uuid}${anamneseFlowSearch({ audience, isPreview })}`;
}

export function rewriteAdultCollectionUrl(url, config) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const [path, search = ""] = url.split("?");
  const params = new URLSearchParams(search);
  const audience = pathAudience(path);
  if (!audience || !hasV2Audience(audience, config)) return url;

  if (audience !== "adulto") {
    params.set("audience", audience);
    const qs = params.toString();
    return `/anamnese-v2${qs ? `?${qs}` : ""}`;
  }

  const suffix = params.toString() ? `?${params.toString()}` : "";
  if (path.startsWith("/mapa-da-saude") || path === "mapa-da-saude") {
    return `/anamnese-v2/mapa${suffix}`;
  }
  return `/anamnese-v2${suffix}`;
}

export async function ensureAnamneseConfig() {
  const previous = readStoredAnamneseConfig();
  try {
    const { data } = await api.get("config/anamnese");
    const payload = data?.data ?? data;
    if (!payload || (payload.version !== "v1" && payload.version !== "v2")) {
      throw new Error("payload inválido");
    }
    return persistAnamneseConfig(payload);
  } catch {
    return previous || defaultAnamneseConfig();
  }
}

export { EVALUATION_STORAGE_KEY };
